import { useEffect, useState } from 'react';

/**
 * Twitter/LinkedIn-style header behavior: collapse on scroll DOWN, expand on
 * scroll UP (or near the top); pausing keeps the current state.
 *
 * We measure distance from the last turning point (an "anchor") rather than
 * per-event deltas, so a slow steady scroll still adds up to a flip — and small
 * jitter never does. After each flip we briefly lock out further flips for the
 * height transition and resync, which absorbs the reflow/clamp scroll events that
 * would otherwise bounce the header at the bottom.
 *
 * Window-scroll based; the caller decides where/whether to apply it (e.g. mobile
 * only). No-ops unless `enabled`.
 */
export function useScrollCollapse(enabled: boolean): boolean {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setCollapsed(false);
      return;
    }

    const THRESHOLD = 12; // distance from the turning point before flipping
    const TOP_ZONE = 56; // always expanded near the very top
    const LOCK_MS = 340; // ~ the header's collapse/expand transition

    let isCollapsed = false; // local mirror (the effect closure is created once)
    let anchor = window.scrollY; // the most-recent direction turning point
    let locked = false;
    let lockTimer: ReturnType<typeof setTimeout> | undefined;

    const flip = (next: boolean, y: number) => {
      isCollapsed = next;
      setCollapsed(next);
      anchor = y;
      locked = true;
      clearTimeout(lockTimer);
      lockTimer = setTimeout(() => {
        locked = false;
        anchor = window.scrollY;
      }, LOCK_MS);
    };

    const onScroll = () => {
      if (locked) return;
      const y = window.scrollY;
      if (y <= TOP_ZONE) {
        if (isCollapsed) flip(false, y);
        else anchor = y;
        return;
      }
      if (!isCollapsed) {
        // Expanded: anchor tracks the lowest scroll point; collapse once we've
        // scrolled DOWN THRESHOLD past it.
        if (y < anchor) anchor = y;
        else if (y - anchor > THRESHOLD) flip(true, y);
      } else {
        // Collapsed: anchor tracks the highest scroll point; expand once we've
        // scrolled UP THRESHOLD past it.
        if (y > anchor) anchor = y;
        else if (anchor - y > THRESHOLD) flip(false, y);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(lockTimer);
    };
  }, [enabled]);

  return collapsed;
}
